// src/pages/Subscription/Subscription.jsx

import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import SubscriptionPlans from '../../components/subscription/SubscriptionPlans';
import toast from 'react-hot-toast';

export default function Subscription() {
  const [showPlans, setShowPlans] = useState(false);
  const { 
    subscription, 
    getCurrentPlan, 
    isActive, 
    cancelSubscription, 
    checkUsageLimit,
    loading 
  } = useSubscription();

  const currentPlan = getCurrentPlan();
  const [cancelling, setCancelling] = useState(false);

  const handleCancelSubscription = async () => {
    if (window.confirm('Aboneliğinizi iptal etmek istediğinizden emin misiniz?')) {
      setCancelling(true);
      try {
        const success = await cancelSubscription();
        if (success) {
          toast.success('Aboneliğiniz başarıyla iptal edildi.');
        } else {
          toast.error('İptal işlemi sırasında bir hata oluştu.');
        }
      } catch (error) {
        console.error('Cancel subscription error:', error);
        toast.error('İptal işlemi sırasında bir hata oluştu.');
      }
      setCancelling(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Belirsiz';
    return new Date(date.toDate()).toLocaleDateString('tr-TR');
  };

  const getUsageInfo = () => {
    const usageTypes = ['orders', 'customers', 'products', 'invoices'];
    const usageLabels = {
      orders: 'Siparişler',
      customers: 'Müşteriler', 
      products: 'Ürünler',
      invoices: 'Faturalar'
    };

    return usageTypes.map(type => {
      const usage = checkUsageLimit(type);
      return {
        type,
        label: usageLabels[type],
        ...usage
      };
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Abonelik bilgileri yükleniyor...</h2>
      </div>
    );
  }

  if (showPlans) {
    return <SubscriptionPlans onClose={() => setShowPlans(false)} />;
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '40px', color: '#333' }}>Abonelik Yönetimi</h1>

      {/* Current Plan Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Mevcut Planınız</h2>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#06C167', fontSize: '24px' }}>
              {currentPlan.name} Plan
            </h3>
            <p style={{ margin: '8px 0', color: '#666' }}>
              {currentPlan.price === 0 ? 'Ücretsiz' : `${currentPlan.price}₺/ay`}
            </p>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 16px',
              borderRadius: '20px',
              backgroundColor: isActive() ? '#28a745' : '#dc3545',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {isActive() ? 'Aktif' : 'Pasif'}
            </div>
          </div>
        </div>

        {subscription && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            <div>
              <strong>Başlangıç Tarihi:</strong>
              <p style={{ margin: '4px 0' }}>{formatDate(subscription.createdAt)}</p>
            </div>
            {subscription.expiresAt && (
              <div>
                <strong>Bitiş Tarihi:</strong>
                <p style={{ margin: '4px 0' }}>{formatDate(subscription.expiresAt)}</p>
              </div>
            )}
            {subscription.status && (
              <div>
                <strong>Durum:</strong>
                <p style={{ margin: '4px 0', textTransform: 'capitalize' }}>{subscription.status}</p>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button
            onClick={() => setShowPlans(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#06C167',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Planı Değiştir
          </button>
          
          {currentPlan.id !== 'free' && isActive() && (
            <button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              style={{
                padding: '12px 24px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: cancelling ? 'default' : 'pointer',
                opacity: cancelling ? 0.7 : 1
              }}
            >
              {cancelling ? 'İptal Ediliyor...' : 'Aboneliği İptal Et'}
            </button>
          )}
        </div>
      </div>

      {/* Usage Statistics */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Kullanım İstatistikleri</h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          {getUsageInfo().map(usage => (
            <div key={usage.type} style={{
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>{usage.label}</h4>
              
              {usage.limit === -1 ? (
                <p style={{ margin: 0, color: '#28a745', fontWeight: 'bold' }}>
                  Sınırsız
                </p>
              ) : (
                <>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span>{usage.current || 0} / {usage.limit}</span>
                    <span style={{
                      color: usage.allowed ? '#28a745' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      {usage.allowed ? '✓' : '⚠️'}
                    </span>
                  </div>
                  
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${Math.min(100, ((usage.current || 0) / usage.limit) * 100)}%`,
                      height: '100%',
                      backgroundColor: usage.allowed ? '#06C167' : '#dc3545'
                    }} />
                  </div>
                  
                  <p style={{ 
                    margin: '8px 0 0 0', 
                    fontSize: '12px', 
                    color: '#666' 
                  }}>
                    {usage.remaining} kalan
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Plan Features */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Plan Özellikleri</h2>
        
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: 0
        }}>
          {currentPlan.features.map((feature, index) => (
            <li key={index} style={{
              padding: '12px 0',
              borderBottom: '1px solid #f1f3f4',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ color: '#28a745', marginRight: '12px', fontSize: '18px' }}>✓</span>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}